
import { defineComponent, computed, onMounted, onBeforeUnmount, ref, nextTick } from 'vue';
import BaseTextarea from './BaseTextarea.vue';

export default defineComponent({
  name: 'LazyBaseTextarea',

  components: {
    BaseTextarea,
  },

  props: {
    // Hériter de toutes les props de BaseTextarea
    modelValue: {
      type: String,
      required: false,
      default: '',
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    rows: {
      type: Number,
      required: false,
      default: 3,
    },
    // Props spécifiques au lazy loading
    loadMode: {
      type: String,
      required: false,
      default: 'auto',
      validator: (value: string) => ['auto', 'click', 'immediate'].includes(value),
    },
    intersectionThreshold: {
      type: Number,
      required: false,
      default: 0.1,
    },
    forceLoad: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['update:modelValue', 'click', 'activate-group', 'deactivate-group'],

  setup(props) {
    const containerRef = ref<HTMLElement>();
    const isLoaded = ref(false);
    let observer: IntersectionObserver | null = null;

    // Contenu tronqué pour l'aperçu
    const truncatedContent = computed(() => {
      if (!props.modelValue) return '';
      const textContent = props.modelValue.replace(/<[^>]*>/g, '');
      return textContent.length > 80 ? `${textContent.substring(0, 80)}...` : textContent;
    });

    const loadComponent = () => {
      if (!isLoaded.value) {
        isLoaded.value = true;
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      }
    };

    const setupIntersectionObserver = () => {
      if (!containerRef.value || typeof IntersectionObserver === 'undefined') {
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= props.intersectionThreshold) {
              loadComponent();
            }
          });
        },
        {
          threshold: props.intersectionThreshold,
          rootMargin: '50px',
        }
      );

      observer.observe(containerRef.value);
    };

    onMounted(() => {
      if (props.forceLoad || props.loadMode === 'immediate') {
        loadComponent();
      } else if (props.loadMode === 'auto') {
        nextTick(() => {
          setupIntersectionObserver();
        });
      }
    });

    onBeforeUnmount(() => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    });

    // Référence vers le composant chargé
    const baseComponentRef = ref();

    // Méthode focus qui redirige vers le composant chargé
    const focus = () => {
      if (isLoaded.value && baseComponentRef.value && baseComponentRef.value.focus) {
        baseComponentRef.value.focus();
      } else if (!isLoaded.value) {
        // Si pas encore chargé, charger d'abord puis focus
        loadComponent();
        nextTick(() => {
          if (baseComponentRef.value && baseComponentRef.value.focus) {
            baseComponentRef.value.focus();
          }
        });
      }
    };

    // Méthodes pour contrôler l'Intersection Observer pendant le scroll
    const pauseObserver = () => {
      if (observer) {
        observer.disconnect();
      }
    };

    const resumeObserver = () => {
      if (!isLoaded.value && containerRef.value) {
        setupIntersectionObserver();
      }
    };

    return {
      containerRef,
      isLoaded,
      loadComponent,
      truncatedContent,
      baseComponentRef,
      focus, // Exposer focus via return
      pauseObserver, // Exposer pour contrôler l'observer
      resumeObserver, // Exposer pour contrôler l'observer
    };
  },
});
